import { useEffect, useRef, useCallback, useMemo, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { createImageHandlerPlugin } from '../lib/imageHandler'
import { SlashCommand } from '../extensions/slashCommand'
import { SelectionToolbar } from './SelectionToolbar'
import { resolveImageUrls, revokeImageUrls, persistImageUrls } from '../db/images'

interface EditorProps {
  noteId: string | null
  content: string
  onChange: (content: string) => void
  onWordCountChange?: (count: number) => void
}

export function Editor({
  noteId,
  content,
  onChange,
  onWordCountChange,
}: EditorProps) {
  const isUpdatingFromProp = useRef(false)
  const activeBlobUrls = useRef<string[]>([])
  const noteIdRef = useRef(noteId)
  noteIdRef.current = noteId

  // Store the latest content in a ref so the load-on-note-switch effect
  // can read it without being in its dependency array.
  const contentRef = useRef(content)
  contentRef.current = content

  // Track blob URL -> image ID mappings for persistence
  const blobToIdMap = useMemo(() => new Map<string, string>(), [])

  const [isEmpty, setIsEmpty] = useState(true)

  const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder: 'Start writing…',
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
      Highlight.configure({
        multicolor: false,
      }),
      Underline,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      SlashCommand.configure({
        imageHandler: {
          getNoteId: () => noteIdRef.current,
          blobToIdMap,
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      if (isUpdatingFromProp.current) return
      let html = editor.getHTML()
      const text = editor.getText()

      setIsEmpty(editor.isEmpty)

      // Convert any blob URLs back to yellowpad:// URLs before saving
      if (blobToIdMap.size > 0) {
        html = persistImageUrls(html, blobToIdMap)
      }

      onChange(html)

      // Calculate word count
      const words = text.trim().split(/\s+/).filter(Boolean).length
      onWordCountChange?.(words)
    },
    editorProps: {
      attributes: {
        class: 'outline-none min-h-full tiptap',
      },
    },
  })

  // Register image paste/drop handler plugin
  useEffect(() => {
    if (!editor) return

    const plugin = createImageHandlerPlugin({
      getNoteId: () => noteIdRef.current,
      blobToIdMap,
    })

    // Add plugin to editor
    const { state } = editor
    const newState = state.reconfigure({
      plugins: [...state.plugins, plugin],
    })
    editor.view.updateState(newState)

    // Cleanup: remove plugin on unmount
    return () => {
      if (editor.isDestroyed) return
      const { state: currentState } = editor
      const filteredPlugins = currentState.plugins.filter((p) => p !== plugin)
      const cleanState = currentState.reconfigure({ plugins: filteredPlugins })
      editor.view.updateState(cleanState)
    }
  }, [editor, blobToIdMap])

  // Resolve image URLs when loading note content
  const loadContent = useCallback(
    async (html: string) => {
      if (!editor) return

      // Revoke old blob URLs
      revokeImageUrls(activeBlobUrls.current)
      activeBlobUrls.current = []
      blobToIdMap.clear() // eslint-disable-line react-hooks/immutability -- intentional reset on note switch

      // Resolve yellowpad:// URLs to blob URLs
      const { html: resolved, blobUrls } = await resolveImageUrls(html)
      activeBlobUrls.current = blobUrls

      // Build reverse map for any resolved images (blob URL -> image ID)
      // Extract from the resolved HTML
      const urlRegex = /(blob:[^\s"]+)/g
      const idRegex = /yellowpad:\/\/image\/([\w-]+)/g
      const originalIds: string[] = []
      let match: RegExpExecArray | null
      while ((match = idRegex.exec(html)) !== null) {
        originalIds.push(match[1])
      }
      const resolvedBlobUrls: string[] = []
      while ((match = urlRegex.exec(resolved)) !== null) {
        resolvedBlobUrls.push(match[0])
      }
      for (let i = 0; i < Math.min(originalIds.length, resolvedBlobUrls.length); i++) {
        blobToIdMap.set(resolvedBlobUrls[i], originalIds[i])
      }

      isUpdatingFromProp.current = true
      editor.commands.setContent(resolved || '')
      setIsEmpty(editor.isEmpty)
      const text = editor.getText()
      const words = text.trim().split(/\s+/).filter(Boolean).length
      onWordCountChange?.(words)
      isUpdatingFromProp.current = false
    },
    [editor, onWordCountChange, blobToIdMap]
  )

  // Update editor content when switching notes (noteId changes)
  // We use contentRef instead of content in deps to avoid re-running
  // on every keystroke (which would reset scroll & cause glitches).
  const prevNoteIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!editor) return
    // Only reload content when the noteId actually changes (switching notes)
    if (noteId === prevNoteIdRef.current) return
    prevNoteIdRef.current = noteId

    loadContent(contentRef.current).then(() => {
      editor?.commands.focus('start')
    })
  }, [noteId, editor, loadContent])

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      revokeImageUrls(activeBlobUrls.current)
      activeBlobUrls.current = []
    }
  }, [])

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden"
      style={{ backgroundColor: 'hsl(var(--color-bg-primary))' }}
    >
      <div className="flex-1 overflow-auto px-4 sm:px-8 pt-0 pb-4 sm:pb-8">
        <div className="max-w-5xl mx-auto">
          <div className="editor-container">
            <div className={`relative ${isMac ? 'is-mac' : 'is-not-mac'}`}>
              <EditorContent editor={editor} />
              {editor && <SelectionToolbar editor={editor} />}
              {/* Secondary placeholder hints */}
              {isEmpty && (
                <div className="editor-placeholder-overlay tiptap">
                  <p className="invisible" aria-hidden="true" style={{ margin: 0, padding: 0, marginBottom: '0.75rem' }}>
                    Start writing...
                  </p>
                  <div className="editor-placeholder-hints">
                    <div className="editor-placeholder-hint">
                      <kbd>/</kbd> <span>for commands</span>
                    </div>
                    <div className="editor-placeholder-hint shortcut-cmd-k">
                      <kbd>{isMac ? '⌘K' : 'Ctrl+K'}</kbd> <span>to search notes</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
